import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

/**
 * Modal priorities (lower number = higher priority, shows first)
 */
export const MODAL_PRIORITIES = {
  POLICY_ACCEPTANCE: 1,
  JOINING_BONUS: 2,
  DAILY_BONUS: 3,
  WELCOME_OFFER: 4,
  PWA_INSTALL: 5,
} as const;

export type ModalId = keyof typeof MODAL_PRIORITIES;

interface QueuedModal {
  id: ModalId;
  priority: number;
}

interface ModalQueueContextType {
  /**
   * Request to show a modal. The modal will be queued and shown when all higher-priority modals are done.
   */
  requestShow: (modalId: ModalId) => void;
  
  /**
   * Notify that a modal has been closed/dismissed. This triggers the next modal in queue.
   */
  notifyDismissed: (modalId: ModalId) => void;
  
  /**
   * Cancel a pending request (e.g., if conditions are no longer met)
   */
  cancelRequest: (modalId: ModalId) => void;
  
  /**
   * Check if a specific modal should currently be visible
   */
  shouldShow: (modalId: ModalId) => boolean;
  
  /**
   * Check if any higher-priority modal is showing or queued
   */
  hasHigherPriorityModal: (modalId: ModalId) => boolean;
  
  /**
   * Get the currently active modal (if any)
   */
  activeModal: ModalId | null;
}

const ModalQueueContext = createContext<ModalQueueContextType | undefined>(undefined);

export const useModalQueue = () => {
  const context = useContext(ModalQueueContext);
  if (context === undefined) {
    throw new Error('useModalQueue must be used within a ModalQueueProvider');
  }
  return context;
};

export const ModalQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Queue of modals waiting to be shown
  const [queue, setQueue] = useState<QueuedModal[]>([]);
  // Currently showing modal
  const [activeModal, setActiveModal] = useState<ModalId | null>(null);
  // Ref for debounce timer
  const processTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track if we've done the initial batch processing
  const hasProcessedInitialBatch = useRef(false);
  // Refs to track current values for use in setTimeout
  const queueRef = useRef(queue);
  const activeModalRef = useRef(activeModal);
  
  // Keep refs in sync
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);
  
  useEffect(() => {
    activeModalRef.current = activeModal;
  }, [activeModal]);

  /**
   * Effect to process the queue whenever it changes or activeModal changes
   * Uses a small delay to batch multiple rapid additions before selecting the highest priority
   */
  useEffect(() => {
    // If a modal is already showing, don't process
    if (activeModal !== null) {
      return;
    }
    
    if (queue.length === 0) {
      return;
    }

    // Clear any existing timer
    if (processTimerRef.current) {
      clearTimeout(processTimerRef.current);
    }

    // Use a delay for initial batch processing to let all contexts add their modals
    // After initial batch, process faster when activeModal becomes null
    const delay = hasProcessedInitialBatch.current ? 50 : 200;
    
    processTimerRef.current = setTimeout(() => {
      // Use refs to get current values (not stale closure values)
      const currentActiveModal = activeModalRef.current;
      const currentQueue = queueRef.current;
      
      // Re-check conditions since state may have changed during delay
      if (currentActiveModal !== null) {
        return;
      }
      
      if (currentQueue.length === 0) {
        return;
      }
      
      // Sort by priority and get the highest priority modal
      const sorted = [...currentQueue].sort((a, b) => a.priority - b.priority);
      const nextModal = sorted[0];
      
      hasProcessedInitialBatch.current = true;
      
      // Remove from queue and set as active
      setQueue(prev => prev.filter(m => m.id !== nextModal.id));
      setActiveModal(nextModal.id);
    }, delay);
    
    return () => {
      if (processTimerRef.current) {
        clearTimeout(processTimerRef.current);
      }
    };
  }, [queue, activeModal]);

  /**
   * Request to show a modal
   */
  const requestShow = useCallback((modalId: ModalId) => {
    const priority = MODAL_PRIORITIES[modalId];
    
    setQueue(prev => {
      // Check if already in queue
      if (prev.some(m => m.id === modalId)) {
        return prev;
      }
      
      return [...prev, { id: modalId, priority }];
    });
  }, []);

  /**
   * Notify that a modal has been dismissed
   */
  const notifyDismissed = useCallback((modalId: ModalId) => {
    // Remove from queue if it was queued but not shown
    setQueue(prev => prev.filter(m => m.id !== modalId));
    
    // Clear active if this modal was active
    setActiveModal(prev => prev === modalId ? null : prev);
  }, []);

  /**
   * Cancel a pending request
   */
  const cancelRequest = useCallback((modalId: ModalId) => {
    setQueue(prev => prev.filter(m => m.id !== modalId));
    
    // If this modal was active, clear it
    setActiveModal(prev => prev === modalId ? null : prev);
  }, []);

  /**
   * Check if a specific modal should be visible
   */
  const shouldShow = useCallback((modalId: ModalId) => {
    return activeModal === modalId;
  }, [activeModal]);

  /**
   * Check if any higher-priority modal is showing or queued
   */
  const hasHigherPriorityModal = useCallback((modalId: ModalId) => {
    const myPriority = MODAL_PRIORITIES[modalId];
    
    // Check if active modal has higher priority
    if (activeModal !== null) {
      const activePriority = MODAL_PRIORITIES[activeModal];
      if (activePriority < myPriority) {
        return true;
      }
    }
    
    // Check if any queued modal has higher priority
    return queue.some(m => m.priority < myPriority);
  }, [activeModal, queue]);

  const value: ModalQueueContextType = {
    requestShow,
    notifyDismissed,
    cancelRequest,
    shouldShow,
    hasHigherPriorityModal,
    activeModal,
  };

  return (
    <ModalQueueContext.Provider value={value}>
      {children}
    </ModalQueueContext.Provider>
  );
};
